import { FC, useMemo } from 'react'

import { Dropdown, FilterButton, textChain, useDropdown } from '@revolut-internal/ui-kit'

import {
  ADDITIONAL_FILTERS_MAX_HEIGHT,
  ADDITIONAL_FILTERS_MIN_WIDTH,
} from 'components/Filters/consts'
import { useChannel } from 'hooks/useChannel'
import { QueueStatsFilterDefinition } from 'types'

import { AdditionalFilterDropdown } from './AdditionalFilterDropdown'
import { useAdditionalFilters } from './AdditionalFiltersContext'
import { CreationIntervalFilter } from './filters/CreationIntervalFilter'
import { DueDateFilter } from './filters/DueDateFilter'
import { LastMessageSentFilter } from './filters/LastMessageSentFilter'
import { calculateSelectedFilters } from './utils/calculateSelectedFilters'
import { prepareFiltersArray } from './utils/prepareFilters'

type AdditionalFiltersProps = {
  filters?: QueueStatsFilterDefinition[]
}

export const AdditionalFilters: FC<AdditionalFiltersProps> = ({ filters }) => {
  const { additionalFilters, clearAdditionalFilters } = useAdditionalFilters()
  const { isChat } = useChannel()
  const dropdown = useDropdown()
  const dropdownTargetProps = dropdown.getTargetProps<HTMLDivElement>()

  const appliedFiltersCount = useMemo(
    () => calculateSelectedFilters(additionalFilters),
    [additionalFilters],
  )

  const arrayOfFilters = useMemo(() => {
    if (!filters?.length) {
      return []
    }

    return prepareFiltersArray(filters)
  }, [filters])

  const isActive = !!appliedFiltersCount

  const handleClear = () => {
    clearAdditionalFilters()
    dropdown.toggle(false)
  }

  return (
    <>
      <FilterButton
        {...dropdown.getAnchorProps()}
        aria-haspopup="listbox"
        active={isActive}
        aria-expanded={dropdown.state}
        useIcon="Filter"
        labelClear="Clear filters"
        role="combobox"
        aria-label="Additional filters"
        onClear={isActive ? handleClear : undefined}
      >
        {textChain('Filters', appliedFiltersCount || null)}
      </FilterButton>

      <Dropdown
        {...dropdownTargetProps}
        role="menu"
        minWidth={ADDITIONAL_FILTERS_MIN_WIDTH}
        maxHeight={ADDITIONAL_FILTERS_MAX_HEIGHT}
        focusTrap
      >
        {arrayOfFilters.map(({ key, label, options }) => (
          <AdditionalFilterDropdown
            key={key}
            filterKey={key}
            label={label}
            options={options}
            onSelect={dropdown.toggle}
          />
        ))}

        <CreationIntervalFilter
          open={dropdownTargetProps.open}
          onClose={dropdownTargetProps.onClose}
        />

        {isChat && (
          <>
            <DueDateFilter
              open={dropdownTargetProps.open}
              onClose={dropdownTargetProps.onClose}
            />

            <LastMessageSentFilter
              open={dropdownTargetProps.open}
              onClose={dropdownTargetProps.onClose}
            />
          </>
        )}
      </Dropdown>
    </>
  )
}
